import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import supabase from '../../lib/supabase';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiShield, FiArrowRight, FiRefreshCw } = FiIcons;

const useIsTabletOrBelow = () => {
  const [ok, setOk] = useState(false);
  useEffect(() => {
    const check = () => setOk(window.innerWidth < 1024);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return ok;
};

const OTPVerificationPage = () => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [timer, setTimer] = useState(30);
  const inputRefs = useRef([]);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const isTabletOrBelow = useIsTabletOrBelow();
  
  const { mobile, type, userData } = location.state || {};

  useEffect(() => {
    if (!mobile) {
      navigate('/login');
      return;
    }

    const interval = setInterval(() => {
      setTimer(prev => prev > 0 ? prev - 1 : 0);
    }, 1000);

    return () => clearInterval(interval);
  }, [mobile, navigate]);

  const handleOtpChange = (index, value) => {
    if (value.length > 1) return;
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    
    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const otpValue = otp.join('');
    
    if (otpValue.length !== 6) {
      toast.error('Please enter complete OTP');
      return;
    }

    setLoading(true);
    try {
      console.log('OTP Verification started:', { mobile, type, otpValue });
      
      // Dev mode: accept any 6-digit OTP
      if (type === 'register') {
        console.log('Processing registration with userData:', userData);
        
        if (!userData) {
          toast.error('Registration data not found. Please register again.');
          navigate('/register');
          return;
        }
        
        // Complete registration with the userData from registration
        const user = {
          id: userData.id,
          ...userData,
          isVerified: true,
          createdAt: new Date().toISOString()
        };
        
        // Restrict customer completion on large screens
        if (user.role === 'customer' && !isTabletOrBelow) {
          toast.error('Customer login/registration is allowed only on mobile or tablet devices.');
          navigate('/login');
          return;
        }

        console.log('Logging in user after registration:', user);
        await login(user);
        toast.success('Registration completed successfully!');
        
        // Navigate based on role
        if (user.role === 'customer') {
          navigate('/customer');
        } else if (user.role === 'operator') {
          navigate('/operator');
        } else {
          navigate('/admin');
        }
      } else {
        console.log('Processing login for mobile:', mobile);
        
        // Login: Find user by mobile number in database
        let resolvedUser = null;
        const m10 = String(mobile).replace(/\D/g, '').slice(-10);
        console.log('Normalized mobile for lookup:', m10);
        
        try {
          // Try to find customer by phone number (exact match on normalized 10-digit phone)
          console.log('Login: Looking up customer by phone:', m10);
          const { data: customer, error: custErr } = await supabase
            .from('customers_h2a')
            .select('*')
            .eq('phone', m10)
            .maybeSingle();

          console.log('Login: Customer query result:', { customer, custErr });

          if (!custErr && customer) {
            console.log('Login: Found customer:', customer);
            resolvedUser = {
              id: customer.user_id,
              name: customer.full_name || 'Customer',
              mobile: customer.phone || mobile,
              email: customer.email || null,
              role: 'customer',
              isVerified: true
            };
            console.log('Login: Resolved customer user:', resolvedUser);
          }

          // If not found as customer, try operator lookup
          if (!resolvedUser) {
            console.log('Login: Looking up operator by phone:', m10);
            const { data: operator, error: opErr } = await supabase
              .from('operators_a7b5c9')
              .select('*')
              .eq('phone', m10)
              .maybeSingle();

            console.log('Login: Operator query result:', { operator, opErr });

            if (!opErr && operator) {
              console.log('Login: Found operator:', operator);
              resolvedUser = {
                id: operator.user_id,
                name: operator.full_name || 'Operator',
                mobile: operator.phone || mobile,
                email: operator.email || null,
                role: 'operator',
                isVerified: true
              };
              console.log('Login: Resolved operator user:', resolvedUser);
            }
          }
        } catch (lookupErr) {
          console.error('Login: Database lookup failed:', lookupErr);
          toast.error('Database connection issue. Please try again.');
          return;
        }

        // If not found in database, check registration data as fallback
        if (!resolvedUser) {
          console.log('Login: User not found in database, checking registration data...');
          
          const registrationData = localStorage.getItem('registration_data');
          if (registrationData) {
            try {
              const regData = JSON.parse(registrationData);
              const regMobile = String(regData.mobile).replace(/\D/g, '').slice(-10);
              
              if (regMobile === m10) {
                console.log('Login: Found user in registration data:', regData);
                resolvedUser = {
                  id: regData.id,
                  name: regData.name || 'User',
                  mobile: regData.mobile,
                  email: regData.email || null,
                  role: regData.role || 'customer',
                  isVerified: true
                };
                
                console.log('Login: Using registration data for user:', resolvedUser);
                localStorage.removeItem('registration_data');
              }
            } catch (e) {
              console.error('Login: Failed to parse registration data:', e);
            }
          }
        }

        if (!resolvedUser) {
          console.log('Login: No user found anywhere for mobile:', m10);
          toast.error('Mobile number not found. Please register first.');
          navigate('/register', { state: { mobile } });
          return;
        }

        // Restrict customer completion on large screens
        if (resolvedUser.role === 'customer' && !isTabletOrBelow) {
          toast.error('Customer login is allowed only on mobile or tablet devices.');
          navigate('/login');
          return;
        }

        console.log('Login: Logging in resolved user:', resolvedUser);
        await login(resolvedUser);
        toast.success('Login successful!');
        
        // Navigate based on role
        if (resolvedUser.role === 'customer') {
          navigate('/customer');
        } else if (resolvedUser.role === 'operator') {
          navigate('/operator');
        } else {
          navigate('/admin');
        }
      }
      
    } catch (error) {
      console.error('OTP verification error:', error);
      toast.error(`Verification failed: ${error.message || 'Please try again.'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setResendLoading(true);
    try {
      // Simulate resend delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      setTimer(30);
      toast.success('OTP resent successfully!');
    } catch (error) {
      toast.error('Failed to resend OTP');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <Card className="p-8">
          <div className="text-center mb-8">
            <div className="mx-auto w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mb-4">
              <SafeIcon icon={FiShield} className="w-8 h-8 text-primary-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Verify OTP
            </h1>
            <p className="text-gray-600">
              Enter the 6-digit code sent to {mobile}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <div className="flex justify-center space-x-3">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={el => inputRefs.current[index] = el}
                    type="text"
                    maxLength="1"
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    className="w-12 h-12 text-center text-lg font-semibold border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                ))}
              </div>
            </div>

            <Button
              type="submit"
              loading={loading}
              className="w-full"
              icon={FiArrowRight}
            >
              Verify OTP
            </Button>
          </form>

          <div className="mt-6 text-center">
            {timer > 0 ? (
              <p className="text-gray-600">
                Resend OTP in {timer}s
              </p>
            ) : (
              <button
                onClick={handleResendOtp}
                disabled={resendLoading}
                className="text-primary-600 hover:text-primary-700 font-medium flex items-center justify-center space-x-2"
              >
                <SafeIcon icon={FiRefreshCw} className={`w-4 h-4 ${resendLoading ? 'animate-spin' : ''}`} />
                <span>Resend OTP</span>
              </button>
            )}
          </div>

          <div className="mt-8 bg-yellow-50 p-4 rounded-lg">
            <p className="text-sm text-yellow-800">
              <strong>Demo Mode:</strong> Any 6-digit code will be accepted.
            </p>
          </div>
        </Card>
      </motion.div>
    </div>
  );
};

export default OTPVerificationPage;